import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  queryOrders,
  getOrder,
  getOrderWithPayloads,
  createOrder,
  cancelOrder,
  recoverOrder,
  executeOrderEvent,
  getOrderNotes,
  createOrderNote,
  getVisStatus,
  getCascadeOrders,
  getOrderStats,
  getOrderTimeline,
  getOrderFailures,
  type OrdersQueryParams,
  type OrderStatsParams,
  type CreateOrderRequest,
  type CreateNoteRequest,
  type TimelineParams,
  type FailuresParams,
} from '../api/orders';

export function useOrders(params: OrdersQueryParams) {
  return useQuery({
    queryKey: ['orders', params],
    queryFn: () => queryOrders(params),
  });
}

const INFINITE_PAGE_SIZE = 50;

export function useInfiniteOrders(params: Omit<OrdersQueryParams, 'offset' | 'limit'>) {
  return useInfiniteQuery({
    queryKey: ['orders', 'infinite', params],
    queryFn: ({ pageParam = 0 }) =>
      queryOrders({ ...params, offset: pageParam, limit: INFINITE_PAGE_SIZE }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.data.length;
      if (nextOffset >= lastPage.total) return undefined;
      return nextOffset;
    },
  });
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: ['orders', id],
    queryFn: () => getOrder(id),
    enabled: !!id,
  });
}

export function useOrderWithPayloads(id: string) {
  return useQuery({
    queryKey: ['orders', id, 'payloads'],
    queryFn: () => getOrderWithPayloads(id),
    enabled: !!id,
  });
}

export function useOrderNotes(urn: string) {
  return useQuery({
    queryKey: ['orders', urn, 'notes'],
    queryFn: () => getOrderNotes(urn),
    enabled: !!urn,
  });
}

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateOrderRequest) => createOrder(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, message }: { urn: string; message?: string }) => cancelOrder(urn, message),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

export function useRecoverOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (urn: string) => recoverOrder(urn),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

export function useExecuteOrderEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, event }: { id: string; event: string }) => executeOrderEvent(id, event),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/**
 * Fetches VIS status for an order.
 * When the VIS status is non-terminal (pending, processing, submitted, etc.),
 * auto-polls every 30 seconds so the UI stays up to date.
 */
export function useVisStatus(id: string, hasVis: boolean, orderStatus?: string) {
  const terminalStatuses = ['completed', 'canceled', 'failed', 'error', 'declined', 'withdrawn'];
  const isTerminal = orderStatus ? terminalStatuses.includes(orderStatus.toLowerCase()) : false;

  return useQuery({
    queryKey: ['orders', id, 'vis-status'],
    queryFn: () => getVisStatus(id),
    enabled: !!id && hasVis,
    refetchInterval: isTerminal ? false : 30_000, // poll every 30s for non-terminal orders
  });
}

export function useCreateOrderNote(urn: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateNoteRequest) => createOrderNote(urn, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders', urn, 'notes'] });
    },
  });
}

export function useOrderStats(params?: OrderStatsParams) {
  return useQuery({
    queryKey: ['orders', 'stats', params],
    queryFn: () => getOrderStats(params),
    refetchInterval: 60_000,
  });
}

/**
 * Fetches time-bucketed order counts for the given time range.
 * Auto-refreshes every 60 seconds.
 */
export function useOrderTimeline(params: TimelineParams) {
  return useQuery({
    queryKey: ['orders', 'stats', 'timeline', params],
    queryFn: () => getOrderTimeline(params),
    refetchInterval: 60_000,
  });
}

/**
 * Fetches recent failed/error orders with details.
 * Auto-refreshes every 60 seconds.
 */
export function useOrderFailures(params?: FailuresParams) {
  return useQuery({
    queryKey: ['orders', 'stats', 'failures', params],
    queryFn: () => getOrderFailures(params),
    refetchInterval: 60_000,
  });
}

/**
 * Fetches all orders in a cascade chain by chainId.
 * Only enabled when a chainId is provided.
 */
export function useCascadeOrders(chainId?: string) {
  return useQuery({
    queryKey: ['orders', 'cascade', chainId],
    queryFn: () => getCascadeOrders(chainId!),
    enabled: !!chainId,
  });
}
